import {
  directionsUrl,
  fetchPickupSales,
  houseLabel,
  type PickupSale,
} from '@auctisync/api-client';
import { formatCents, formatInZone, type CalendarEvent } from '@auctisync/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AddToCalendar } from '../../components/AddToCalendar';
import { EmptyState } from '../../components/bidder-web';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

/**
 * Pickup and collection.
 *
 * NEW SECTION on the website (web design handoff, 2026-09-02). The phone got
 * this screen on 2026-09-02; the website had nothing — a bidder who had won
 * lots could find out when to collect only by opening an invoice and reading a
 * line inside it. So the answer to "when am I collecting?" lived inside a
 * document about money, once per sale, and somebody collecting from two sales
 * had to reconcile two of them.
 *
 * 🔴 NOTHING HERE IS INVENTED. `fetchPickupSales` — the same assembly the phone
 * calls — resolves the auction's own details over the invoice's prose over the
 * house's standing hours, field by field. Where a house has set nothing, this
 * says so and still shows what it does know: which lots are waiting, and the
 * invoice they are on. A screen that confidently states a time nobody entered
 * is how a bidder drives to a closed yard.
 *
 * ✅ THE "ADD TO CALENDAR" BUTTON IS HERE NOW (2026-09-18). It was impossible
 * for as long as pickup was only PROSE — "Sat 9–4" cannot say which Saturday,
 * and an entry on the wrong one is worse than no button because the bidder then
 * believes they have a reminder. The auction form's collection scheduler writes
 * `auctions.pickup_dates`, so `pickup.nextWindow` can be a real instant, and the
 * button is gated on exactly that: a scheduled time or nothing.
 *
 * 🔴 THE TIME SHOWN IS THE WAREHOUSE'S, NOT THE READER'S. Jim, 2026-09-18:
 * times *"should always show the auction's local time"*. A buyer in another
 * state still has to arrive when the gate is open, so the zone is named beside
 * the time rather than quietly converted to theirs.
 */
export function AccountPickup() {
  const { userId } = useSession();
  const { selected, active } = useBidderHouses();

  const [sales, setSales] = useState<PickupSale[]>([]);
  const [loading, setLoading] = useState(true);

  const tenantId = selected?.tenant_id ?? active?.tenant_id ?? null;
  const houseName = active === null ? 'The auction house' : houseLabel(active);
  const paddle = selected?.tenant_bidder_number ?? null;

  useEffect(() => {
    if (userId === null) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const built = await fetchPickupSales(supabase, {
        userId,
        tenantId,
      });
      if (cancelled) return;
      setSales(built);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, tenantId]);

  return (
    <>
      <div className="wa-hd">
        <div>
          <h1 className="w-h1" style={{ fontSize: 32 }}>
            Pickup and collection
          </h1>
          <p className="muted" style={{ marginTop: 6, fontSize: 14 }}>
            What to collect, and when.
          </p>
        </div>
        {/* THE PADDLE NUMBER IS WHAT THE COUNTER ASKS FOR, and it is a real
            figure — `tenant_bidder_number`, the number the house itself calls
            out — not a placeholder. It is the single thing most likely to be
            wanted while standing at a desk. */}
        {paddle !== null ? (
          <div style={{ textAlign: 'right' }}>
            <div className="w-lbl">Your bidder number</div>
            <div className="w-price" style={{ fontSize: 26 }}>
              {paddle}
            </div>
          </div>
        ) : null}
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : sales.length === 0 ? (
        <EmptyState
          icon="▤"
          title="Nothing is waiting for collection"
          body="Lots you win appear here with the collection window as soon as the sale is invoiced."
          action={
            <Link to="/browse" className="w-btn w-btn-out">
              Browse the lots
            </Link>
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {sales.map((sale, i) => {
            const directions = directionsUrl(sale.pickup.address, 'android');
            return (
              <section key={sale.invoiceId}>
                {i === 0 ? (
                  <div className="wa-band" style={{ marginBottom: 14 }}>
                    <div className="w-lbl">Next window · {houseName}</div>
                    <div
                      className="w-h2"
                      style={{ marginTop: 6, color: 'var(--text-on-deep)', fontSize: 22 }}
                    >
                      {windowLabel(sale.pickup)}
                    </div>
                    {sale.pickup.address !== null ? (
                      <p
                        style={{
                          margin: '6px 0 0',
                          color: 'var(--text-label-on-deep)',
                          fontSize: 14,
                        }}
                      >
                        {[sale.pickup.location, sale.pickup.address].filter(Boolean).join(' · ')}
                      </p>
                    ) : null}
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        marginTop: 16,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      {directions !== null ? (
                        <a
                          href={directions}
                          target="_blank"
                          rel="noreferrer"
                          className="w-btn w-btn-ghost w-btn-sm"
                        >
                          Directions
                        </a>
                      ) : null}
                      <AddToCalendar event={calendarEvent(sale, houseName)} />
                    </div>
                  </div>
                ) : null}

                <div className="wa-card">
                  <div className="wa-row">
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <b style={{ display: 'block', fontWeight: 600 }}>{sale.auctionTitle}</b>
                      <span className="muted" style={{ fontSize: 13 }}>
                        {i === 0
                          ? `${String(sale.lines.length)} lot${sale.lines.length === 1 ? '' : 's'} to collect`
                          : windowLabel(sale.pickup)}
                      </span>
                    </span>
                    <Link
                      to={`/my/invoices/${sale.invoiceId}`}
                      className="w-btn w-btn-out w-btn-sm"
                    >
                      {sale.invoiceNumber ?? 'Invoice'}
                    </Link>
                  </div>
                  {sale.lines.map((line) => (
                    <div className="wa-row" key={line.id}>
                      <span className="w-lbl" style={{ minWidth: 54 }}>
                        Lot {line.lot_number ?? '—'}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>{line.description}</span>
                      <span className="mono">{formatCents(line.line_total_cents)}</span>
                    </div>
                  ))}
                  {sale.pickup.instructions !== null ? (
                    <div className="wa-row">
                      <span className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
                        {sale.pickup.instructions}
                      </span>
                    </div>
                  ) : null}
                  {sale.pickup.from === 'none' ? (
                    <div className="wa-row">
                      <span className="muted" style={{ fontSize: 13.5, lineHeight: 1.55 }}>
                        {houseName} has not published collection details for this sale yet. Your
                        lots are held for you — ask them on the lot page or from Help.
                      </span>
                    </div>
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * The line a bidder reads to know when to turn up.
 *
 * 🔴 A SCHEDULED TIME IS SHOWN IN THE SALE'S ZONE, NAMED. `resolvePickup` puts
 * prose in `window` when there is no scheduled time, and prose is passed
 * through untouched — it is the house's own words and we do not know what clock
 * they meant. A scheduled time we DO know, so it is formatted here rather than
 * accepting the pre-rendered string, which was built without a zone.
 */
function windowLabel(pickup: PickupSale['pickup']): string {
  if (pickup.nextWindow !== null) {
    const start = formatInZone(pickup.nextWindow.startsAt, pickup.timeZone);
    if (pickup.nextWindow.endsAt === null) return start;
    // The end repeats the zone only once — "9:00 AM – 4:00 PM CDT" reads better
    // than naming it twice, and both halves are in the same zone by definition.
    const end = formatInZone(pickup.nextWindow.endsAt, pickup.timeZone, {
      hour: 'numeric',
      minute: '2-digit',
      withZone: true,
    });
    return `${formatInZone(pickup.nextWindow.startsAt, pickup.timeZone, { dateStyle: 'medium', timeStyle: 'short', withZone: false })} – ${end}`;
  }
  return pickup.window ?? 'Collection times not set yet';
}

/**
 * The calendar entry, or null when there is nothing datable.
 *
 * 🔴 NULL IS THE COMMON CASE AND IS CORRECT. Most sales still describe
 * collection in words, and `AddToCalendar` renders nothing for null rather than
 * offering a button that would guess a date.
 */
function calendarEvent(sale: PickupSale, houseName: string): CalendarEvent | null {
  const w = sale.pickup.nextWindow;
  if (w === null) return null;

  const where = [sale.pickup.location, sale.pickup.address]
    .filter((p) => p !== null && p !== '')
    .join(', ');

  return {
    title: `Collect your lots — ${houseName}`,
    startsAt: w.startsAt,
    endsAt: w.endsAt,
    location: where === '' ? null : where,
    description: [
      sale.auctionTitle,
      w.note,
      sale.pickup.instructions,
      // The house's own clock, spelled out, so the entry still makes sense to
      // somebody reading it in a different zone weeks later.
      `Collection opens ${formatInZone(w.startsAt, sale.pickup.timeZone)}.`,
    ]
      .filter((p) => p !== null && p !== '')
      .join('\n\n'),
    url: typeof window === 'undefined' ? null : `${window.location.origin}/my/pickup`,
  };
}
